import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'ramda';
import { Layout } from 'layouts';
import { PageTitle } from 'components/misc';
import { Error, HugggLife, Reaction } from 'components';
import { Divider, useThemeContext } from '@huggg/react-components';
import { PoweredByHuggg as UIPoweredByHuggg } from 'components/ui';
import {
  RedeemMethodPinContainer,
  RedeemMethodVoucherContainer
} from 'components/Redeem';
import { withLocations, withHuggg, withRedirect } from 'hoc';
import { useAnalytics } from 'hooks';
import { getBasePathname } from 'utilities/huggg';
import styles from './redeem.module.scss';
import { isInIframe } from 'utilities/iframes';
import { isRedeeming } from 'redux/modules/redeem';
import {
  getSelectedSegment,
  setSelectedSegment
} from 'redux/modules/selectedSegment';
import { getRedeemedSegment, getSegments } from 'redux/modules/huggg';
import {
  getShowHugggLife,
  getShowReaction,
  getFirstRedemption
} from 'components/Redeem/Method/Voucher/utils';

export const ScreensRedeem = props => {
  const {
    hasVoucherToClaim,
    huggg,
    isBlueHuggg,
    isPinRedeem,
    isRedeemed,
    isValidRedemptionMethod,
    segments,
    setSelectedSegment,
    vouchers
  } = props;
  const analytics = useAnalytics();
  const { name: theme, shouldShowPoweredByHuggg } = useThemeContext();

  const hasVouchers = useMemo(() => Boolean(vouchers && vouchers.length), [
    vouchers
  ]);
  const hasReaction = useMemo(() => huggg?.hasReaction, [huggg.hasReaction]);

  const backAction = useCallback(() => getBasePathname(huggg), [huggg]);

  const shouldShowHugggLife = useMemo(
    () =>
      getShowHugggLife({
        theme,
        isBlueHuggg,
        isRedeemed,
        hasVouchers,
        isInIframe
      }),
    [theme, isBlueHuggg, isRedeemed, hasVouchers]
  );

  const isFirstRedemption = useMemo(() => getFirstRedemption(huggg), [huggg]);

  const shouldShowReaction = useMemo(
    () =>
      getShowReaction({
        hasReaction,
        isFirstRedemption,
        hasVoucherToClaim,
        isRedeemed,
        hasVouchers
      }),
    [hasReaction, isRedeemed, isFirstRedemption, hasVouchers, hasVoucherToClaim]
  );

  useEffect(() => {
    analytics.screenViewedHugggRedeem();
  }, [analytics]);

  useEffect(() => {
    if (segments?.length === 1) {
      setSelectedSegment(segments[0]);
    }
  }, [segments, setSelectedSegment]);

  return (
    <Layout backAction={backAction}>
      <PageTitle huggg={huggg} />

      <div data-test-id="screen-redeem">
        {isValidRedemptionMethod ? (
          <>
            <div>
              {isPinRedeem ? (
                <RedeemMethodPinContainer data-test-id="redeem-method-pin" />
              ) : (
                <RedeemMethodVoucherContainer data-test-id="redeem-method-voucher" />
              )}
              {shouldShowPoweredByHuggg && <UIPoweredByHuggg />}
            </div>
          </>
        ) : (
          <Error
            data-test-id="redeem-method-unknown"
            errorMessage="Unknown redemption method"
          />
        )}
      </div>

      <Divider className={styles.divider} />

      {shouldShowReaction && (
        <div className={styles.reaction}>
          <Reaction includeCommentSection={true} isDialog />
        </div>
      )}

      {shouldShowHugggLife && (
        <div className={styles.hugggLifeContainer}>
          <HugggLife />
        </div>
      )}
    </Layout>
  );
};

ScreensRedeem.propTypes = {
  hasVoucherToClaim: PropTypes.bool.isRequired,
  huggg: PropTypes.object.isRequired,
  isBlueHuggg: PropTypes.bool.isRequired,
  isFormRedeemed: PropTypes.bool.isRequired,
  isMultiRedeemable: PropTypes.bool.isRequired,
  isPinRedeem: PropTypes.bool.isRequired,
  isValidRedemptionMethod: PropTypes.bool.isRequired,
  isRedeemable: PropTypes.bool.isRequired,
  isRedeemed: PropTypes.bool.isRequired,
  isRedeeming: PropTypes.bool.isRequired,
  isTestHuggg: PropTypes.bool.isRequired,
  segments: PropTypes.array.isRequired,
  setSelectedSegment: PropTypes.func.isRequired,
  vouchers: PropTypes.array
};

export const redeemedRedirectHandler = props => {
  return [
    props.isRedeemed || props.isFetchedHuggg || props.hasFulfilledVoucher,
    `${getBasePathname(props.huggg)}/redeemed`
  ];
};

export const redeemableRedirectHandler = props => [
  !props.isRedeemable,
  `${getBasePathname(props.huggg)}/stash`
];

export const chooseRedirectHandler = props => {
  const { segments, huggg, selectedSegment, redeemedSegment } = props;
  const isMultiSegment = segments?.length > 1;

  return [
    isMultiSegment && !selectedSegment && !redeemedSegment,
    `${getBasePathname(huggg)}/choose`
  ];
};

const augmentedWithHuggg = screen =>
  withHuggg(screen, { forceRefresh: { huggg: true } });

const mapStateToProps = state => ({
  isRedeeming: isRedeeming(state),
  segments: getSegments(state),
  selectedSegment: getSelectedSegment(state),
  redeemedSegment: getRedeemedSegment(state)
});

export default compose(
  augmentedWithHuggg,
  connect(mapStateToProps, { setSelectedSegment }),
  withLocations,
  withRedirect(redeemedRedirectHandler),
  withRedirect(redeemableRedirectHandler),
  withRedirect(chooseRedirectHandler)
)(ScreensRedeem);
